import { audioPlayerStyles, backgroundColors } from "@ai-teach/canvas-styles";
import type { AudioPlayerElement } from "@ai-teach/types";
import { useState } from "react";
import { PiSpeakerHighFill } from "react-icons/pi";

const PREVIEW_PROGRESS = 0.3;

// The icon fills its wrapper so it scales with the card. display:block kills the
// inline baseline gap that would otherwise offset the overlay from the base icon.
const ICON_FILL: React.CSSProperties = { display: "block", width: "100%", height: "100%" };

interface AudioPlayerRendererProps {
  element: AudioPlayerElement;
  isLiveMode?: boolean;
}

export function AudioPlayerRenderer({ element, isLiveMode }: AudioPlayerRendererProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioRef, setAudioRef] = useState<HTMLAudioElement | null>(null);
  // Playback progress as a 0~1 ratio, driven by the audio timeupdate event.
  const [progress, setProgress] = useState(0);

  const handlePlayPause = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (audioRef) {
      if (isPlaying) {
        audioRef.pause();
      } else {
        audioRef.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <div
      style={{
        ...audioPlayerStyles.outerDiv,
        flexDirection: "column",
        pointerEvents: "auto",
        userSelect: "none",
      }}
    >
      <audio
        ref={setAudioRef}
        src={element.media.src ?? ""}
        // Only the live-mobile-preview panel should actually autoplay.
        autoPlay={isLiveMode && element.autoPlay}
        loop={element.loop}
        controls={false}
        onEnded={() => {
          setIsPlaying(false);
          setProgress(0);
        }}
        onTimeUpdate={(e) => {
          // currentTime and duration are in seconds; guard against divide-by-zero.
          const { currentTime, duration } = e.currentTarget;
          setProgress(duration > 0 ? currentTime / duration : 0);
        }}
      >
        <track kind="captions" />
      </audio>

      {/* Play/Pause button — the speaker icon itself doubles as the progress bar */}
      <button
        type="button"
        onClick={handlePlayPause}
        aria-label={isPlaying ? "Pause" : "Play"}
        style={{ ...audioPlayerStyles.iconWrapper, padding: 0, border: "none", background: "none" }}
      >
        <PiSpeakerHighFill color={backgroundColors.gray} style={ICON_FILL} />
        <span
          style={{
            position: "absolute",
            inset: 0,
            // Crop the orange copy from the right; inset() keeps it full size, so
            // the icon is revealed left to right instead of being squashed.
            clipPath: `inset(0 ${(1 - (isLiveMode ? PREVIEW_PROGRESS : progress)) * 100}% 0 0)`,
            transition: "clip-path 0.25s linear",
          }}
        >
          <PiSpeakerHighFill color={backgroundColors.orange} style={ICON_FILL} />
        </span>
      </button>
    </div>
  );
}
